/**
 * Auth Deep Link Service
 * 
 * Native iOS/Android platformlarda OAuth callback URL'lerini yakalar,
 * token'ları parse eder ve Supabase oturumu oluşturur.
 * Web/PWA'da sessizce no-op olarak çalışır.
 */
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { supabase } from '@/lib/supabase'
import { globalNavigate } from '@/utils/navigation'
import { logError, logWarn } from '@/lib/log'

const CALLBACK_PATH = 'login-callback'
const CUSTOM_SCHEME = 'gunumvar'

/**
 * URL fragment (#) veya query string (?) içinden token parametrelerini parse eder.
 * Supabase implicit flow: redirect#access_token=...&refresh_token=...
 */
function parseTokensFromUrl(urlString: string): { accessToken: string; refreshToken: string } | null {
  try {
    // Fragment (#) kısmını parse et — Supabase implicit flow bunu kullanır
    const hashIndex = urlString.indexOf('#')
    if (hashIndex === -1) return null

    const fragment = urlString.substring(hashIndex + 1)
    const params = new URLSearchParams(fragment)

    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')

    if (!accessToken || !refreshToken) {
      logWarn('[AuthDeepLink] Token(lar) URL\'de bulunamadı')
      return null
    }

    return { accessToken, refreshToken }
  } catch (error) {
    logError('[AuthDeepLink] URL parse hatası:', error)
    return null
  }
}

/**
 * Deep link URL'ini işler: token parse → session oluştur → tarayıcıyı kapat
 */
async function handleDeepLink(url: string): Promise<void> {
  // Sadece bizim callback URL'imizi işle
  if (!url.includes(`${CUSTOM_SCHEME}://${CALLBACK_PATH}`)) return

  const tokens = parseTokensFromUrl(url)
  if (!tokens) {
    logError('[AuthDeepLink] Token parse başarısız — URL:', url)
    return
  }

  try {
    const { error } = await supabase.auth.setSession({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
    })

    if (error) {
      logError('[AuthDeepLink] Oturum oluşturma hatası:', error.message)
      return
    }
  } catch (error) {
    logError('[AuthDeepLink] Beklenmeyen hata:', error)
  } finally {
    // In-app tarayıcıyı kapat
    try {
      await Browser.close()
    } catch {
      // Browser zaten kapalı olabilir — sessiz geç
    }
  }
}

/**
 * Native platformlarda deep link dinleyicisini başlatır.
 * App başlangıcında (main.tsx) bir kez çağrılır.
 * Web/PWA'da hiçbir şey yapmaz.
 */
export function initAuthDeepLinkListener(): void {
  if (!Capacitor.isNativePlatform()) return

  App.addListener('appUrlOpen', ({ url }) => {

    // 1. Auth callback handling
    if (url.includes(`${CUSTOM_SCHEME}://${CALLBACK_PATH}`)) {
      handleDeepLink(url)
      return
    }

    // 2. Universal Link handling (https://app.gunumvar.com.tr/invite/...)
    try {
      const urlObj = new URL(url)
      const path = urlObj.pathname

      // Davet linkleri → /invite/:code
      if (path.startsWith('/invite/')) {
        const code = path.replace('/invite/', '').split('?')[0]
        if (code) {
          // React Router'a yönlendir
          globalNavigate(`/invite/${code}${urlObj.search}`)
          return
        }
      }

      // Grup detay linkleri → /groups/active?id=...
      if (path.startsWith('/groups/')) {
        globalNavigate(`${path}${urlObj.search}`)
        return
      }

      // Diğer deep linkler → doğrudan yönlendir
      if (urlObj.hostname === 'app.gunumvar.com.tr') {
        globalNavigate(`${path}${urlObj.search}`)
      }
    } catch {
      // URL parse edilemezse sessiz geç
    }
  })
}

/**
 * Native Apple Sign In — ASAuthorizationController (native iOS sheet) kullanır.
 * SFSafariViewController yerine native sheet açılır → beyaz ekran sorunu çözülür.
 * 
 * Apple, kullanıcının adını yalnızca İLK yetkilendirmede paylaşır.
 * Sonraki girişlerde name=null gelir. Bu yüzden ilk girişte name yakalanıp kaydedilir.
 */
export async function performNativeAppleSignIn(): Promise<{ error: Error | null }> {
  try {
    const { SocialLogin } = await import('@capgo/capacitor-social-login')

    const result = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['email', 'name'],
      },
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = result?.result as any
    const idToken = response?.idToken || response?.identityToken
    if (!idToken) {
      return { error: new Error('Apple ID token alınamadı') }
    }

    // Supabase'e native Apple token ile giriş yap
    const { error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: idToken,
    })

    if (error) {
      logError('[AuthDeepLink] Apple signInWithIdToken hatası:', error.message)
      return { error }
    }

    // İlk girişte name verisi yakalama (Apple sonraki girişlerde name göndermez)
    const givenName = response?.givenName || response?.name?.givenName
    const familyName = response?.familyName || response?.name?.familyName
    if (givenName) {
      const fullName = `${givenName} ${familyName || ''}`.trim()
      await supabase.auth.updateUser({
        data: { full_name: fullName },
      }).catch(() => {
        // Name kaydetme başarısız olursa kritik değil — sessiz geç
      })
    }
    return { error: null }
  } catch (error) {
    const msg = String(error)
    // Kullanıcı iptal etti — hata değil
    if (msg.includes('cancel') || msg.includes('1001') || msg.includes('Cancel')) {
      return { error: null }
    }
    logError('[AuthDeepLink] Native Apple Sign In hatası:', error)
    return { error: error instanceof Error ? error : new Error(msg) }
  }
}

/**
 * SocialLogin eklentisini uygulama açılışında bir kez başlatır — YALNIZ iOS'ta.
 *
 * Android'de eklenti Apple için `redirectUrl` istiyor; verilmediği için her açılışta
 * "apple.android.redirectUrl is null or empty" reddi Sentry'ye gidiyordu
 * (GUNUM-VAR-1V, 11 günde 13 kez). Android'de Apple girişi tarayıcı akışıyla yapılır.
 */
export async function initNativeSocialLogin(): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return
  try {
    const { SocialLogin } = await import('@capgo/capacitor-social-login')
    await SocialLogin.initialize({ apple: { clientId: 'com.gunumvar.app' } })
  } catch (err) {
    // Initialize başarısız olursa uygulama çalışmaya devam etmeli
    logWarn('[App] SocialLogin init hatası (non-critical):', err)
  }
}

/**
 * Native platformlarda OAuth akışını başlatır.
 * Apple → Native ASAuthorizationController (beyaz ekran düzeltmesi)
 * Google → In-app tarayıcı (SFSafariViewController) — 60s timeout ile
 */
export async function performNativeOAuth(provider: 'google' | 'apple', emailHint?: string): Promise<{ error: Error | null }> {
  // Apple → iOS'ta native flow (SFSafariViewController yerine ASAuthorizationController).
  // Android'de eklenti başlatılmıyor (GUNUM-VAR-1V); Apple da Google gibi tarayıcıdan açılır.
  if (provider === 'apple' && Capacitor.getPlatform() === 'ios') {
    return performNativeAppleSignIn()
  }

  // Google (ve Android'de Apple) → browser flow (EC-3 riski nedeniyle değiştirilmedi)
  try {
    const redirectTo = `${CUSTOM_SCHEME}://${CALLBACK_PATH}`

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: true,
        queryParams: {
          prompt: 'select_account',
          ...(emailHint && { login_hint: emailHint }),
        },
      },
    })

    if (error) return { error }

    if (!data?.url) {
      return { error: new Error('OAuth URL alınamadı') }
    }

    // In-app tarayıcıyı aç (SFSafariViewController / Chrome Custom Tab)
    await Browser.open({
      url: data.url,
      presentationStyle: 'fullscreen',
      windowName: '_self',
    })

    // 60s timeout — tarayıcı akışı tamamlanmazsa otomatik kapat
    setTimeout(async () => {
      try {
        await Browser.close()
      } catch {
        // Browser zaten kapalı — sessiz geç
      }
    }, 60_000)

    return { error: null }
  } catch (error) {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }
}

